import { useParams } from 'react-router-dom'
import { Spinner, TenantImageEnum, useAssetsContext, useMediaScreen } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import {
  Center,
  Flex,
  Image,
  Stack,
  Title,
  Text,
  Progress,
  Badge,
  Button,
  TypographyStylesProvider
} from 'summon-ui/mantine'
import useQuestsCategories from '@features/quests/hooks/useQuestCategories'
import useQuests from '@features/quests/hooks/useQuests'
import { Quest } from '@generated/generates'
import { useViewer } from '@hooks'
import Breadcrumbs from '@ui/Breadcrumbs'
import { useWeb3Modal } from '@web3modal/wagmi/react'
import QuestContainer from '../components/QuestContainer'

const QuestCategoryDetailsView = ({ category }: { category?: string }) => {
  const t = useTranslation()
  const { categoryId: categoryParamsId } = useParams()
  const { isAuthenticated } = useViewer()
  const isMaxTablet = useMediaScreen({
    type: 'max',
    breakpoint: 'tablet'
  })
  const { open: openConnectModal } = useWeb3Modal()
  const categoryId = category || categoryParamsId
  const { getTenantImage } = useAssetsContext()
  const questPlaceholder = getTenantImage(TenantImageEnum.QuestPlaceholder)
  const { quests, isLoadingQuests } = useQuests({ categoryId: categoryId! })
  const { categoryById } = useQuestsCategories({ categoryId: categoryId! })

  const isCategoryCompleted = categoryById?.completionPercentage === 100

  if (isLoadingQuests) {
    return (
      <Center h='100%' w='100%'>
        <Spinner />
      </Center>
    )
  }

  return (
    <Stack gap='xl' pb='xl'>
      {!category && <Breadcrumbs lastText={categoryById?.name || 'Loading'} />}
      {categoryById && (
        <>
          <Flex direction={isMaxTablet ? 'column' : 'row'} gap='lg' align='center'>
            <Image
              data-testid='category-hero-image'
              alt='quest'
              src={categoryById?.imageUrl || questPlaceholder}
              radius='md'
              w={isMaxTablet ? '100%' : 500}
            />
            <Stack gap='sm' w={isMaxTablet ? '100%' : '50%'}>
              <Title data-testid='category-name-label' fw='bold' lineClamp={2}>
                {categoryById?.name}
              </Title>
              {isCategoryCompleted && (
                <Badge bg='green.9' size='lg'>
                  {t('Completed')}
                </Badge>
              )}
              <TypographyStylesProvider>
                <Text
                  {...(!isMaxTablet ? { lineClamp: 5 } : {})}
                  data-testid='category-description-label'
                  dangerouslySetInnerHTML={{ __html: categoryById?.description || '' }}
                />
              </TypographyStylesProvider>

              {isAuthenticated ? (
                <Flex align='center' gap='sm'>
                  <Progress size='lg' className='w-full' value={categoryById?.completionPercentage || 0} />
                  <Title order={4}>{categoryById?.completionPercentage}%</Title>
                </Flex>
              ) : (
                <Button onClick={() => openConnectModal()} maw={160}>
                  {t('LOGIN / SIGN UP')}
                </Button>
              )}
            </Stack>
          </Flex>
        </>
      )}

      <Stack>{quests?.map((quest) => <QuestContainer key={quest.id} quest={quest as Quest} />)}</Stack>
    </Stack>
  )
}

export default QuestCategoryDetailsView
